import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import VenueContext from '../../contexts/VenueContext';
import { BellIcon } from '../../components/BellIcon';
import NotificationBellButton from '../../components/NotificationBellButton';
import { MessengerChatShell } from '../../components/messaging/MessengerChatShell';
import { MessengerComposer } from '../../components/messaging/MessengerComposer';
import { MessengerMessageActionSheet } from '../../components/messaging/MessengerMessageActionSheet';
import { MessengerMessageBubble } from '../../components/messaging/MessengerMessageBubble';
import { MessengerMessageInfoSheet } from '../../components/messaging/MessengerMessageInfoSheet';
import { getMessengerGroupMeta } from '../../components/messaging/messengerUtils';
import { buildReplyPayload, copyTextToClipboard, resolveOwnStatusVariant, summarizeMessageText } from '../../components/messaging/messengerThreadUtils';
import { buildVenueConversationMap } from '../../utils/venueDemoMessages';
import { buildMessageHeaders } from '../../utils/messageHeaders';
import { formatChatDisplayName } from '../../utils/nameFormatters';
import { SupportClosedBanner, renderSupportSystemMessage } from '../../components/messaging/SupportSystemCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VenueChatPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { convoId } = useParams();
  const venueCtx = useContext(VenueContext);
  const activeVenue = venueCtx?.activeVenue;
  const activeVenueAppId = venueCtx?.activeVenueAppId;
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const effectiveVenueAppId = activeVenueAppId || localStorage.getItem('activeVenueAppId') || storedUser.venue_id || null;
  const venueConversations = useMemo(() => buildVenueConversationMap(activeVenue), [activeVenue?.id, activeVenue?.restaurant_name]);
  const chat = useMemo(() => venueConversations[convoId] || {
    name: activeVenue?.restaurant_name || 'Unknown',
    messages: [],
    image_url: null,
  }, [activeVenue?.restaurant_name, convoId, venueConversations]);
  const [newMessage, setNewMessage] = useState('');
  const [messages, setMessages] = useState(chat.messages || []);
  const [chatName, setChatName] = useState(formatChatDisplayName(chat.name) || chat.name);
  const [chatImage, setChatImage] = useState(chat.image_url || null);
  const [loading, setLoading] = useState(Boolean(effectiveVenueAppId));
  const [actorId, setActorId] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [actionTarget, setActionTarget] = useState(null);
  const [infoTarget, setInfoTarget] = useState(null);
  const [supportStatus, setSupportStatus] = useState(null);
  const [supportConvoType, setSupportConvoType] = useState(null);
  const isSupportConvo = supportConvoType === 'support';
  const supportClosed = isSupportConvo && supportStatus === 'closed';
  // Support threads always render as "Allure" + Allure logo so admin
  // replies read as one consistent "Allure" identity (not the admin's
  // personal name/photo).
  const SUPPORT_CONTACT_NAME = 'Allure';
  const SUPPORT_CONTACT_IMAGE = '/notification-icon-192.png';
  const displayedChatName = isSupportConvo ? SUPPORT_CONTACT_NAME : chatName;
  const displayedChatImage = isSupportConvo ? SUPPORT_CONTACT_IMAGE : chatImage;

  const logVenueChatError = useCallback((message, error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error(message, error);
    }
  }, []);

  useEffect(() => {
    setMessages(chat.messages || []);
    setChatName(formatChatDisplayName(chat.name) || chat.name);
    setChatImage(chat.image_url || null);
    setActorId(null);
  }, [chat.image_url, chat.name, convoId]);

  const fetchConversation = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const headers = effectiveVenueAppId
        ? buildMessageHeaders(token, effectiveVenueAppId, 'venue')
        : { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/messages/conversations/${convoId}`, { headers });
      setMessages(response.data.messages || []);
      setChatName(formatChatDisplayName(response.data.other_user) || formatChatDisplayName(chat.name) || chat.name);
      setChatImage(response.data.other_user?.image_url || response.data.other_user?.photo_url || null);
      setActorId(response.data.conversation?.participants?.find((participantId) => participantId !== response.data.other_user?.id) || null);
      setSupportStatus(response.data.conversation?.support_status || null);
      setSupportConvoType(response.data.conversation?.type || null);
    } catch (error) {
      logVenueChatError('Failed to load venue/staff chat', error);
      toast.error(error.response?.data?.detail || 'Failed to load this conversation.');
    } finally {
      setLoading(false);
    }
  }, [chat.name, convoId, effectiveVenueAppId, logVenueChatError]);

  useEffect(() => {
    fetchConversation();
  }, [fetchConversation]);

  // Live-refresh on incoming Socket.IO `conversation_message_received` event.
  useEffect(() => {
    const handler = (event) => {
      if (event?.detail?.conversation_id && event.detail.conversation_id !== convoId) return;
      fetchConversation();
    };
    window.addEventListener('allure:conversation-message-received', handler);
    return () => window.removeEventListener('allure:conversation-message-received', handler);
  }, [convoId, fetchConversation]);

  // WhatsApp-style tick flip on venue/staff outgoing messages when the
  // other side acks delivered / read. Patches state in place.
  useEffect(() => {
    const applyStatus = (kind) => (event) => {
      const detail = event?.detail || {};
      if (detail.conversation_id && detail.conversation_id !== convoId) return;
      const ids = new Set(detail.message_ids || []);
      if (!ids.size) return;
      setMessages((prev) => prev.map((message) => {
        if (!ids.has(message.id)) return message;
        if (kind === 'delivered') return { ...message, delivered: true, delivered_at: detail.at || message.delivered_at };
        return { ...message, read: true, read_at: detail.at || message.read_at, delivered: true, delivered_at: message.delivered_at || detail.at };
      }));
    };
    const delivered = applyStatus('delivered');
    const read = applyStatus('read');
    window.addEventListener('allure:conversation-messages-delivered', delivered);
    window.addEventListener('allure:conversation-messages-read', read);
    return () => {
      window.removeEventListener('allure:conversation-messages-delivered', delivered);
      window.removeEventListener('allure:conversation-messages-read', read);
    };
  }, [convoId]);

  const sendMessage = useCallback(async ({ content = '', messageType = 'text', metadata = {}, previewText = '' }) => {
    if (!content.trim() && messageType === 'text') {
      return;
    }

    const enrichedMetadata = replyingTo ? { ...metadata, reply_to: replyingTo } : metadata;

    if (!effectiveVenueAppId) {
      setMessages((prev) => [...prev, {
        id: Date.now(),
        from: 'me',
        text: content,
        content,
        message_type: messageType,
        metadata: enrichedMetadata,
      }]);
      setReplyingTo(null);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/messages/send`,
        {
          conversation_id: convoId,
          content,
          message_type: messageType,
          metadata: enrichedMetadata,
          preview_text: previewText,
        },
        { headers: buildMessageHeaders(token, effectiveVenueAppId, 'venue') },
      );
      setMessages((prev) => [...prev, response.data]);
      setReplyingTo(null);
    } catch (error) {
      logVenueChatError('Failed to send venue/staff message', error);
      toast.error(error.response?.data?.detail || 'Failed to send the message.');
      throw error;
    }
  }, [convoId, effectiveVenueAppId, logVenueChatError, replyingTo]);

  const handleSendText = useCallback(async (text) => {
    await sendMessage({ content: text, messageType: 'text', previewText: text });
    setNewMessage('');
  }, [sendMessage]);

  const handleSendImage = useCallback(async (metadata) => {
    await sendMessage({ content: '', messageType: 'image', metadata, previewText: metadata.source === 'camera' ? 'Sent a photo' : 'Sent an image' });
  }, [sendMessage]);

  const handleSendVideo = useCallback(async (metadata) => {
    await sendMessage({ content: '', messageType: 'video', metadata, previewText: 'Sent a video' });
  }, [sendMessage]);

  const handleSendAudio = useCallback(async (metadata) => {
    await sendMessage({ content: '', messageType: 'audio', metadata, previewText: 'Sent a voice message' });
  }, [sendMessage]);

  // ---- Message actions (react / copy / pin / edit / unsend) -----------------
  const applyUpdatedMessage = useCallback((updatedMessage) => {
    setMessages((prev) => prev.map((message) => (message.id === updatedMessage.id ? updatedMessage : message)));
  }, []);

  const reactToMessage = useCallback(async (message, emoji, silent = false) => {
    try {
      const token = localStorage.getItem('token');
      const currentReaction = message?.metadata?.reactions?.[actorId] || '';
      const nextEmoji = currentReaction === emoji ? '' : emoji;
      const response = await axios.post(`${API}/messages/${message.id}/reaction`, { emoji: nextEmoji }, { headers: buildMessageHeaders(token, effectiveVenueAppId, 'venue') });
      applyUpdatedMessage(response.data);
      setActionTarget(null);
    } catch (error) {
      if (!silent) toast.error(error.response?.data?.detail || 'Failed to react to the message.');
    }
  }, [actorId, applyUpdatedMessage, effectiveVenueAppId]);

  const copyMessage = useCallback(async (message) => {
    const copied = await copyTextToClipboard(summarizeMessageText(message));
    if (copied) toast.success('Copied');
    else toast.error('Unable to copy this message.');
    setActionTarget(null);
  }, []);

  const pinMessage = useCallback(async (message, pinned = true) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/messages/${message.id}/pin`, { pinned }, { headers: buildMessageHeaders(token, effectiveVenueAppId, 'venue') });
      setMessages((prev) => prev.map((item) => {
        if (item.id === response.data.id) return response.data;
        return { ...item, metadata: { ...(item.metadata || {}), pinned: false } };
      }));
      setActionTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pin the message.');
    }
  }, [effectiveVenueAppId]);

  const unsendMessage = useCallback(async (message) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/messages/${message.id}/unsend`, {}, { headers: buildMessageHeaders(token, effectiveVenueAppId, 'venue') });
      setMessages((prev) => prev.filter((item) => item.id !== message.id));
      if (replyingTo?.id === message.id) setReplyingTo(null);
      setActionTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unsend the message.');
    }
  }, [effectiveVenueAppId, replyingTo?.id]);

  const backPath = location.pathname.startsWith('/staff-admin/')
    ? '/staff-admin/share'
    : location.pathname.startsWith('/staff-app/')
      ? '/staff-app/share'
      : '/venue/share';

  const composer = supportClosed ? (
    <SupportClosedBanner
      onOpenNewTicket={() => navigate('/support/contact')}
      dataTestId="venue-chat-support-closed-banner"
    />
  ) : (
    <MessengerComposer
      value={newMessage}
      onChange={(event) => setNewMessage(event.target.value)}
      onSendText={handleSendText}
      onSendImage={handleSendImage}
      onSendVideo={handleSendVideo}
      onSendAudio={handleSendAudio}
      replyingTo={replyingTo}
      onCancelReply={() => setReplyingTo(null)}
      editingMessage={editingMessage}
      onCancelEdit={() => { setEditingMessage(null); setNewMessage(''); }}
      onSaveEdit={async (message, text) => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(`${API}/messages/${message.id}/edit`, { content: text }, { headers: buildMessageHeaders(token, effectiveVenueAppId, 'venue') });
          setMessages((prev) => prev.map((item) => (item.id === response.data.id ? response.data : item)));
          setEditingMessage(null);
          setNewMessage('');
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to edit the message.');
        }
      }}
      placeholder="Message"
      dataTestIdPrefix="venue-chat"
    />
  );

  return (
    <>
      <MessengerChatShell
        pageTestId="venue-chat-page"
        bellNode={<NotificationBellButton buttonTestId="venue-chat-bell-button" popupTestId="venue-chat-notification-popup" />}
        onBack={() => navigate(backPath)}
        backTestId="venue-chat-back-button"
        contactName={displayedChatName}
        contactImage={displayedChatImage}
        contactTestId="venue-chat-contact-avatar"
        showCallButton={false}
        messages={messages}
        loading={loading}
        emptyText="No messages yet"
        composer={composer}
        scrollDependency={messages.length}
        renderMessage={(message, index) => {
          const isMine = message.from === 'me' || (actorId && message.sender_id === actorId);
          const messageKey = `${message.id}-${message.created_at || message.sender_id || 'message'}`;
          const supportRender = renderSupportSystemMessage(message, {
            conversation: { id: convoId, type: supportConvoType, support_status: supportStatus },
            isMine,
            onResolved: () => fetchConversation(),
            dataTestId: `venue-chat-support-${messageKey}`,
          });
          if (supportRender) return supportRender.node;
          const groupMeta = getMessengerGroupMeta(messages, index, (candidate) => ((candidate.from === 'me' || (actorId && candidate.sender_id === actorId)) ? 'me' : 'other'));

          const myPhoto = storedUser.photo_url
            ? (storedUser.photo_url.startsWith('/') ? `${BACKEND_URL}${storedUser.photo_url}` : storedUser.photo_url)
            : (storedUser.image_url || null);

          return (
            <MessengerMessageBubble
              key={messageKey}
              message={{ ...message, content: message.text || message.content || '' }}
              isMine={Boolean(isMine)}
              avatarName={isMine ? 'Me' : displayedChatName}
              avatarImage={isMine ? myPhoto : displayedChatImage}
              dataTestId={`venue-chat-message-${messageKey}`}
              showAvatar={!groupMeta.nextSameSender}
              groupPosition={groupMeta.groupPosition}
              spacingMode="venue"
              statusVariant={isMine ? resolveOwnStatusVariant(message) : null}
              onReply={(targetMessage, mine) => setReplyingTo(buildReplyPayload(targetMessage, mine ? 'You' : displayedChatName))}
              onLongPress={(targetMessage, mine) => setActionTarget({ message: targetMessage, isMine: mine })}
              onDoubleTapHeart={(targetMessage) => reactToMessage(targetMessage, '❤️', true)}
            />
          );
        }}
      />
      <MessengerMessageActionSheet
        open={Boolean(actionTarget)}
        message={actionTarget?.message}
        isMine={Boolean(actionTarget?.isMine)}
        onClose={() => setActionTarget(null)}
        onReact={(emoji) => reactToMessage(actionTarget.message, emoji)}
        onCopy={(message) => copyMessage(message)}
        onInfo={(message) => { setInfoTarget({ message, isMine: Boolean(actionTarget?.isMine) }); setActionTarget(null); }}
        onPin={(message) => pinMessage(message)}
        onReply={(message) => { setReplyingTo(buildReplyPayload(message, actionTarget?.isMine ? 'You' : displayedChatName)); setActionTarget(null); }}
        onEdit={(message) => { setEditingMessage(message); setNewMessage(message.content || ''); setReplyingTo(null); setActionTarget(null); }}
        onUnsend={(message) => unsendMessage(message)}
      />
      <MessengerMessageInfoSheet
        open={Boolean(infoTarget)}
        message={infoTarget?.message}
        isMine={Boolean(infoTarget?.isMine)}
        currentUser={{ id: actorId, name: 'You', image_url: storedUser.photo_url || storedUser.image_url }}
        otherUser={{ id: null, name: displayedChatName, image_url: displayedChatImage }}
        onClose={() => setInfoTarget(null)}
        dataTestId="venue-message-info-sheet"
      />
    </>
  );
}
